import { BoundingBox, getHeight, getWidth } from './BoundingBox.utils';

import { IMAGE_COMING_SOON, MAX_IMAGE_EXPORT_BYTES } from '~/core/constants';

interface CanvasDimensions {
  width: number;
  height: number;
  leftOffset: number;
  topOffset: number;
}

/**
 * Properties required for generating the image URL.
 */
export interface CanvasImageUrlProps {
  canvas: fabric.Canvas;
  format: 'jpeg' | 'png';
  scaleFactor: number;
  leftOffset: number;
  topOffset: number;
  width: number;
  height: number;
}

/**
 * Given a current maximum dimension and a current "file size", proportionally scale to attempt to fit a maxFileSize
 * without increasing the dimension in size
 * @param maxDimension current maximum dimension (width or height)
 * @param fileSize currrent file size - if <= maxRawImageSize then no change
 * @param maxRawImageSize maximum file size target
 * @return new maxDimension - will never get larger
 */
export const scaleMaxDimension = (
  maxDimension: number,
  fileSize: number,
  maxRawImageSize = MAX_IMAGE_EXPORT_BYTES
): number =>
  fileSize > 0 && fileSize > maxRawImageSize
    ? maxDimension * (maxRawImageSize / fileSize)
    : maxDimension;

/**
 * calculate the scaling value based on the smaller of the maxDimension to width and height ratio
 * @param width actual canvas width
 * @param height actual canvas height
 * @param maxDimension maximum target dimension in either direction
 */
export const dimensionScaler = (
  width: number,
  height: number,
  maxDimension: number
): number => Math.max(Math.min(maxDimension / width, maxDimension / height), 1);

/**
 * Computes image dimensions based on the canvas and its bounding box.
 * Returns undefined if the computed width or height is non-positive.
 */
export const getCanvasImageDimensions = (
  canvas: fabric.Canvas,
  box: BoundingBox
): CanvasDimensions | undefined => {
  let leftOffset = 0;
  let topOffset = 0;
  let width = getWidth(box);
  let height = getHeight(box);

  if (width <= 0 || height <= 0) {
    return undefined;
  }

  if (canvas.vptCoords?.tl && canvas.viewportTransform) {
    const { tl } = canvas.vptCoords;

    if (typeof tl.x === 'number' && typeof tl.y === 'number') {
      leftOffset = (box.left - tl.x) * canvas.viewportTransform[0];
      topOffset = (box.top - tl.y) * canvas.viewportTransform[3];
    }
    width *= canvas.viewportTransform[0];
    height *= canvas.viewportTransform[3];
  }

  return width <= 0 || height <= 0
    ? undefined
    : { width, height, leftOffset, topOffset };
};

/**
 * Returns an image URL of the canvas using the provided export parameters.
 * If an error occurs (e.g. due to cross-origin issues), falls back to a placeholder.
 */
export const makeCanvasImageUrl = ({
  canvas,
  format,
  scaleFactor,
  leftOffset,
  topOffset,
  width,
  height,
}: CanvasImageUrlProps): string => {
  try {
    return canvas.toDataURL({
      format,
      multiplier: scaleFactor,
      quality: format === 'png' ? undefined : 0.7,
      left: leftOffset,
      top: topOffset,
      width,
      height,
    });
  } catch (error) {
    return IMAGE_COMING_SOON;
  }
};
